import { Injectable } from "@angular/core";
import { Router, ActivatedRouteSnapshot } from "@angular/router";

// Auth Services
import { AuthService } from "src/app/shared/services/auth.service";

@Injectable({ providedIn: "root" })
export class AuthGuard {
  constructor(private authService: AuthService, private router: Router) {}

  permissions: Array<any> = [];
  autologinCompleted: boolean = false;

  private isPopstate(): boolean {
    return this.router.getCurrentNavigation()?.trigger === "popstate";
  }

  canActivateChild(route: ActivatedRouteSnapshot): boolean {
    if (this.isPopstate()) return !!this.authService.logginUserToken;
    const checkPermission = route.data["auth"];

    if (this.authService.logginUserToken) {
      if (!checkPermission || this.authService.isMainAdminLogin) {
        return true;
      }
      this.authService.authPermission.subscribe((permissions: any) => {
        this.permissions = permissions;
        return this.managePermission(permissions, checkPermission);
      });
    }
    return false;
  }

  managePermission(permissions: any, checkPermission: any): boolean {
    if (permissions?.length > 0) {
      if (this.permissions && !this.findGrant(this.permissions, checkPermission)) {
        let adminRoute = "";
        for (const permission of this.permissions) {
          if (permission.route) {
            adminRoute = permission.route;
            break;
          }
        }
        // A role that grants nothing (or failed to load) leaves no landing page. Navigating to "" would
        // bounce straight back through this guard, so stay put and let the empty sidebar show instead.
        if (adminRoute && !this.isPopstate()) {
          this.router.navigate([adminRoute]);
        }
      }
      return this.hasPermission(checkPermission);
    }
    return false;
  }

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    if (this.isPopstate()) return !!this.authService.logginUserToken;
    const checkPermission = route.data["auth"];
    if (!this.autologinCompleted) {
      await this.authService.autologin();
      this.autologinCompleted = true;
    }
    if (this.authService.logginUserToken) {
      if (!checkPermission || this.authService.isMainAdminLogin) {
        return true;
      }
      return this.hasPermission(checkPermission);
    }
    return false;
  }

  /**
   * Resolves a route's `data.auth` against the granted permission list.
   *
   * The create-booking flow nests its business-type screens under the parent page
   * ("create-booking/book-ride", "create-booking/quick-commerce", ...). Those are reached only from the
   * parent, so they inherit its grant rather than needing an entry of their own — otherwise every
   * business type would have to be checkboxed separately in the role editor for no gain.
   */
  private findGrant(permissions: any[], checkPermission: any): any {
    const exact = permissions?.find((x) => x.name === checkPermission);
    if (exact) {
      return exact;
    }
    const parent = String(checkPermission).split("/")[0];
    return parent === checkPermission ? undefined : permissions?.find((x) => x.name === parent);
  }

  hasPermission(checkPermission: any): boolean {
    this.authService.authPermission.subscribe((permissions: any) => {
      if (this.permissions.length === 0) {
        this.permissions = permissions;
        if (this.permissions && !this.findGrant(this.permissions, checkPermission)) {
          let route = "";
          for (const permission of this.permissions) {
            if (permission.route) {
              route = permission.route;
              break;
            }
          }
          // Same reason as above: no granted page means nowhere to redirect to.
          if (route && !this.isPopstate()) {
            this.router.navigate([route]);
          }
        }
      }
      return !!this.findGrant(this.permissions, checkPermission);
    });
    return !!this.findGrant(this.permissions, checkPermission);
  }
}
